import { useEffect, useState } from "react";
import { regions, sortOptions, years } from "@/mocks/categories";
import type { SortKey } from "@/utils/video";

type FilterBarProps = {
  year: string;
  region: string;
  sort: SortKey;
  resultCount: number;
  onChange: (patch: { year?: string; region?: string; sort?: SortKey }) => void;
  onReset: () => void;
};

const chipBase =
  "focus-ring cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-medium transition-colors md:text-xs";

export default function FilterBar({
  year,
  region,
  sort,
  resultCount,
  onChange,
  onReset,
}: FilterBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const activeCount =
    (year !== "全部" ? 1 : 0) + (region !== "全部" ? 1 : 0) + (sort !== "latest" ? 1 : 0);

  const renderChipGroup = (
    options: string[],
    current: string,
    onPick: (value: string) => void,
    labelPrefix: string,
  ) => (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={`${labelPrefix}-${option}`}
          type="button"
          onClick={() => onPick(option)}
          aria-pressed={current === option}
          className={`${chipBase} ${
            current === option
              ? "border-primary-500 bg-primary-500 text-background-50"
              : "border-background-300/80 bg-white text-foreground-600 hover:border-secondary-300 hover:text-secondary-700"
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );

  const renderSortGroup = (compact = false) => (
    <div className="flex flex-wrap gap-2">
      {sortOptions.map((option) => (
        <button
          key={option.key}
          type="button"
          onClick={() => onChange({ sort: option.key })}
          aria-pressed={sort === option.key}
          className={`${chipBase} ${
            sort === option.key
              ? `${compact ? "border-accent-500 bg-accent-500" : "border-secondary-500 bg-secondary-500"} text-primary-950`
              : "border-background-300/80 bg-white text-foreground-600 hover:border-secondary-300 hover:text-secondary-700"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div className="surface-card p-3 md:p-4">
        <div className="mb-3 flex items-center justify-between gap-2 border-b border-background-100 pb-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="focus-ring inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300/80 bg-white px-2.5 py-1.5 text-[11px] font-medium text-foreground-700 md:hidden"
            >
              <i className="ri-filter-3-line text-sm" aria-hidden="true" />
              筛选
              {activeCount > 0 ? (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] text-primary-950">
                  {activeCount}
                </span>
              ) : null}
            </button>
            <p className="hidden items-center gap-1.5 text-xs text-foreground-500 md:flex">
              <i className="ri-database-2-line text-sm text-foreground-400" aria-hidden="true" />
              共 <span className="font-semibold text-foreground-900">{resultCount}</span> 部内容符合当前条件
            </p>
          </div>
          <button
            type="button"
            onClick={onReset}
            className="focus-ring inline-flex cursor-pointer items-center gap-1 rounded text-[11px] text-foreground-500 transition-colors hover:text-secondary-700"
          >
            <i className="ri-refresh-line text-sm" aria-hidden="true" />
            重置筛选
          </button>
        </div>

        <div className="hidden space-y-3 md:block">
          <div className="flex flex-wrap items-start gap-3">
            <span className="w-12 shrink-0 pt-1.5 text-[11px] font-medium text-foreground-400">
              年份
            </span>
            {renderChipGroup(years, year, (value) => onChange({ year: value }), "year")}
          </div>
          <div className="flex flex-wrap items-start gap-3">
            <span className="w-12 shrink-0 pt-1.5 text-[11px] font-medium text-foreground-400">
              地区
            </span>
            {renderChipGroup(regions, region, (value) => onChange({ region: value }), "region")}
          </div>
          <div className="flex flex-wrap items-start gap-3">
            <span className="w-12 shrink-0 pt-1.5 text-[11px] font-medium text-foreground-400">
              排序
            </span>
            {renderSortGroup()}
          </div>
        </div>

        <div className="md:hidden">
          <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onChange({ sort: option.key })}
                className={`${chipBase} shrink-0 ${
                  sort === option.key
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300/80 bg-white text-foreground-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {drawerOpen ? (
        <div className="md:hidden">
          <button
            type="button"
            aria-label="关闭筛选面板"
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-[70] cursor-pointer bg-primary-950/50"
          />
          <div className="fixed inset-y-0 right-0 z-[75] flex w-[86%] max-w-sm animate-slide-in flex-col bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-4">
              <div>
                <h2 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
                <p className="mt-0.5 text-[11px] text-foreground-500">
                  共 {resultCount} 部内容符合当前条件
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="focus-ring inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-700"
                aria-label="关闭筛选面板"
              >
                <i className="ri-close-line text-lg" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-5">
              <div className="mb-6">
                <h3 className="mb-2.5 text-xs font-semibold text-foreground-800">年份</h3>
                {renderChipGroup(years, year, (value) => onChange({ year: value }), "m-year")}
              </div>
              <div className="mb-6">
                <h3 className="mb-2.5 text-xs font-semibold text-foreground-800">地区</h3>
                {renderChipGroup(regions, region, (value) => onChange({ region: value }), "m-region")}
              </div>
              <div>
                <h3 className="mb-2.5 text-xs font-semibold text-foreground-800">排序方式</h3>
                {renderSortGroup(true)}
              </div>
            </div>

            <div className="flex gap-2 border-t border-background-200 px-4 py-4">
              <button
                type="button"
                onClick={() => {
                  onReset();
                  setDrawerOpen(false);
                }}
                className="focus-ring w-1/3 cursor-pointer rounded-md border border-background-300/80 bg-white px-4 py-2.5 text-xs font-medium text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="focus-ring w-2/3 cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 text-xs font-semibold text-background-50"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}